import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

/**
 * RL-994 / RL-1672 chat with the clinic from the patient app: the branch's
 * nurses share one inbox (`clinic_chat:read`) and reply or close
 * (`clinic_chat:reply`).
 */
export type ClinicChatStatus = 'open' | 'answered' | 'closed';

interface StaffRef {
  id: string;
  fullName: string;
}

/** The chat without its messages (GET /clinic-chats). */
export interface ClinicChatHead {
  id: string;
  status: ClinicChatStatus;
  patient: { id: string; fullName: string };
  /** The app created this record and the identity is not verified yet. */
  identityPending: boolean;
  branch: { id: string; name: string };
  handledBy: StaffRef | null;
  /** The patient's last message while the chat waits for a nurse. */
  waitingSince: string | null;
  /** Written outside 8 am to 10 pm (branch time). */
  receivedOutOfHours: boolean;
  answeredAt: string | null;
  closedAt: string | null;
  closedBy: StaffRef | null;
  closeReason: 'account_deleted' | null;
  lastMessageAt: string;
  /** RL-1672: the patient's messages since a nurse last opened the chat. */
  unreadCount?: number;
  staffReadAt?: string | null;
  createdAt: string;
  version: number;
}

export interface ClinicChatListItem extends ClinicChatHead {
  messageCount: number;
  /** RL-1672: the newest message (cut to 120 characters). */
  lastMessage?: {
    authorType: 'patient' | 'staff';
    text: string;
    sentAt: string;
  } | null;
}

export interface ClinicChatMessage {
  id: string;
  authorType: 'patient' | 'staff';
  staff: StaffRef | null;
  text: string;
  sentAt: string;
}

export interface ClinicChat extends ClinicChatHead {
  messages: ClinicChatMessage[];
}

/** The list filter: Open (waiting or answered) and Closed. */
export type ChatFilter = 'open' | 'closed';

function useBranchKey() {
  const { selected, canSelectAll } = useBranch();
  return selected?.id ?? (canSelectAll ? 'all' : 'none');
}

export const CHATS_KEY = ['clinic-chats'] as const;

/** GET /clinic-chats for the selected branch; Open = not closed. */
export function useClinicChats(filter: ChatFilter) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...CHATS_KEY, 'list', filter, branch],
    queryFn: async ({ signal }) => {
      const rows = (
        await api.get<ClinicChatListItem[]>('/clinic-chats', {
          signal,
          query: { status: filter === 'closed' ? 'closed' : 'all' },
        })
      ).data;
      return filter === 'closed'
        ? rows
        : rows
            .filter((c) => c.status !== 'closed')
            // Waiting for a nurse first (oldest first), then answered.
            .sort((a, b) =>
              a.status !== b.status
                ? a.status === 'open'
                  ? -1
                  : 1
                : a.status === 'open'
                  ? a.lastMessageAt.localeCompare(b.lastMessageAt)
                  : b.lastMessageAt.localeCompare(a.lastMessageAt),
            );
    },
    enabled: branch !== 'none',
    refetchInterval: 30_000,
  });
}

/** GET /clinic-chats/{id}: one chat with its messages. */
export function useClinicChat(id: string | null) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...CHATS_KEY, 'one', id, branch],
    queryFn: async ({ signal }) =>
      (await api.get<ClinicChat>(`/clinic-chats/${id}`, { signal })).data,
    enabled: !!id && branch !== 'none',
    // Live updates come from the stream; this is the fallback.
    refetchInterval: 30_000,
  });
}

/** Reply (optionally closing), close, and mark read. */
export function useClinicChatActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: CHATS_KEY });
    void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
  };
  return {
    reply: useMutation({
      mutationFn: async (v: {
        id: string;
        text: string;
        version: number;
        close?: boolean;
      }) =>
        (
          await api.post<ClinicChat>(`/clinic-chats/${v.id}/reply`, {
            text: v.text,
            version: v.version,
            ...(v.close ? { close: true } : {}),
          })
        ).data,
      onSuccess: refresh,
    }),
    close: useMutation({
      mutationFn: async (v: { id: string; version: number }) =>
        (
          await api.post<ClinicChat>(`/clinic-chats/${v.id}/close`, {
            version: v.version,
          })
        ).data,
      onSuccess: refresh,
    }),
    // Review: read only up to the newest message on screen (`upTo`).
    read: useMutation({
      mutationFn: async (v: { id: string; upTo?: string }) =>
        (
          await api.post<ClinicChat>(
            `/clinic-chats/${v.id}/read`,
            v.upTo ? { upTo: v.upTo } : {},
          )
        ).data,
      onSuccess: refresh,
    }),
  };
}
